import { useState } from 'react'
import { Campo, Casilla, Entrada } from '@/components/ui/Campo'
import { useAvisar } from '@/components/ui/avisador-contexto'
import {
  esMailValido,
  invitacionEntrada,
  type InvitacionEntrada,
  type ProyectoAdmin,
} from '@/lib/schemas/admin'
import { mailInvitacion, mailSumado, sinFormato } from '@/lib/schemas/mails'
import { useInvitarPersona, useMailYaEsUsuario } from './api'
import { formatearPeriodo } from './formato'
import { useFormulario } from './formulario'
import { ModalMarco } from './ModalMarco'

interface ModalInvitarProps {
  abierto: boolean
  alCerrar: () => void
  clienteId: string
  clienteNombre: string
  /** Quien firma el mail: la profesional asignada al cliente (DEC-10). */
  remitente: string
  /** Proyectos activos del cliente; el actual arranca marcado. */
  proyectos: ProyectoAdmin[]
  proyectoActualId: string
}

export const AVISO_GOOGLE =
  'Tiene que ser una cuenta de Google. Un correo de otro proveedor no va a poder ver el contenido.'
export const AVISO_MAIL_EXISTENTE =
  'Este mail ya tiene acceso a otros proyectos. Le vamos a sumar este.'

/**
 * C8 · Invitar persona (§8.7, diferencia 14). Crea el acceso al portal;
 * el mail sale desde la casilla de la profesional asignada (DEC-10). No toca
 * permisos de Drive.
 */
export function ModalInvitar(props: ModalInvitarProps) {
  if (!props.abierto) return null
  return <Formulario key={props.proyectoActualId} {...props} />
}

function Formulario({
  abierto,
  alCerrar,
  clienteId,
  clienteNombre,
  remitente,
  proyectos,
  proyectoActualId,
}: ModalInvitarProps) {
  const invitar = useInvitarPersona()
  const yaEsUsuario = useMailYaEsUsuario()
  const avisar = useAvisar()
  const [valores, setValores] = useState<InvitacionEntrada>(() =>
    vacio(clienteId, proyectoActualId),
  )
  const [existente, setExistente] = useState(false)
  const form = useFormulario(
    invitacionEntrada,
    async (datos, crudos) => {
      await invitar.mutateAsync(crudos)
      avisar(`${datos.nombre} ya tiene acceso. Le mandamos el mail de invitación.`)
    },
    alCerrar,
  )

  const revisarMail = () => {
    const email = valores.email.trim()
    if (!esMailValido(email)) {
      setExistente(false)
      return
    }
    yaEsUsuario(email)
      .then(setExistente)
      .catch(() => {
        setExistente(false)
      })
  }

  const alternarProyecto = (id: string, marcado: boolean) => {
    setValores((a) => ({
      ...a,
      proyectoIds: marcado
        ? [...new Set([...a.proyectoIds, id])]
        : a.proyectoIds.filter((x) => x !== id),
    }))
  }

  const nombre = valores.nombre.trim() || '…'
  // El mismo texto que manda el servidor (DEC-18): el comienzo, hasta el botón.
  const vistaPrevia = existente
    ? mailSumado({
        nombre,
        cliente: clienteNombre,
        proyecto: proyectos
          .filter((pr) => valores.proyectoIds.includes(pr.id))
          .map((pr) => pr.nombre)
          .join(' y '),
        firma: remitente,
        url: '',
      })
    : mailInvitacion({
        nombre,
        email: valores.email.trim() || 'su cuenta de Google',
        cliente: clienteNombre,
        firma: remitente,
        url: '',
      })
  const hastaBoton = vistaPrevia.bloques.findIndex((b) => b.tipo === 'boton')
  const textos = vistaPrevia.bloques
    .slice(0, hastaBoton)
    .flatMap((b) => (b.tipo === 'parrafo' ? [sinFormato(b.texto)] : []))
  const boton = vistaPrevia.bloques[hastaBoton]

  return (
    <ModalMarco
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Invitar persona"
      guardar="Enviar invitación"
      ocupado={form.ocupado}
      error={form.errorGeneral}
      alCambiar={form.limpiar}
      alEnviar={() => {
        form.validarYEnviar(valores)
      }}
    >
      <Campo etiqueta="Nombre" error={form.errores.nombre}>
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.nombre}
            onChange={(e) => {
              setValores((a) => ({ ...a, nombre: e.target.value }))
            }}
            autoFocus
            maxLength={120}
            autoComplete="name"
          />
        )}
      </Campo>

      <Campo
        etiqueta="Mail de Google"
        ayuda={existente ? AVISO_MAIL_EXISTENTE : AVISO_GOOGLE}
        error={form.errores.email}
      >
        {(p) => (
          <Entrada
            id={p.id}
            type="email"
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.email}
            onChange={(e) => {
              setValores((a) => ({ ...a, email: e.target.value }))
            }}
            onBlur={revisarMail}
            autoComplete="email"
            autoCapitalize="none"
            inputMode="email"
            placeholder="nombre@gmail.com"
          />
        )}
      </Campo>

      <fieldset>
        <legend className="mb-1 text-sm font-medium">Proyectos</legend>
        {proyectos.map((pr) => (
          <Casilla
            key={pr.id}
            etiqueta={pr.nombre}
            detalle={`· ${formatearPeriodo(pr.periodoDesde, pr.periodoHasta)}`}
            checked={valores.proyectoIds.includes(pr.id)}
            onCheckedChange={(v) => {
              alternarProyecto(pr.id, v === true)
            }}
          />
        ))}
        {form.errores.proyectoIds && (
          <p role="alert" className="mt-1 text-xs text-nuevo">
            {form.errores.proyectoIds}
          </p>
        )}
      </fieldset>

      <section
        aria-label="Vista previa del mail"
        className="rounded-2xl bg-lienzo px-4 py-3.5 text-sm"
      >
        <p className="text-xs text-tinta-suave">Vista previa del mail · de {remitente}</p>
        <p className="mt-1.5 font-semibold">{vistaPrevia.asunto}</p>
        <div className="mt-1 flex flex-col gap-1 text-tinta-suave">
          {textos.map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
        {boton?.tipo === 'boton' && (
          <span className="mt-3 block rounded-full bg-tinta py-2.5 text-center text-white">
            {boton.texto}
          </span>
        )}
      </section>
    </ModalMarco>
  )
}

const vacio = (clienteId: string, proyectoActualId: string): InvitacionEntrada => ({
  clienteId,
  nombre: '',
  email: '',
  proyectoIds: [proyectoActualId],
})
